<script lang="ts" setup>
import { endpoints } from '~/config/endpoints.config'

definePageMeta({ hasSubnav: false })

const route = useRoute()
const localePath = useLocalePath()
const { t } = useI18n()

// Mobile navigation toggle
const isMobileNavOpen = ref(false)

// Group endpoints by category
const groupedEndpoints = computed(() => {
  const groups: Record<string, typeof endpoints> = {}

  endpoints.forEach((endpoint) => {
    const category = endpoint.id.split('-')[0] // Extract category from id (e.g., 'companies' from 'companies-search')

    if (!groups[category]) {
      groups[category] = []
    }

    groups[category].push(endpoint)
  })

  return groups
})

// Create navigation structure
const apiLinks = computed(() => {
  return [
    // Manual "Get Started" section
    {
      active: route.path.endsWith('/api'),
      icon: 'i-heroicons-book-open',
      iconActive: route.path.endsWith('/api') || route.path.endsWith('/authentication') || route.path.endsWith('/errors') || route.path.endsWith('/rate-limits') || route.path.endsWith('/webhooks'),
      label: t('api.get-started.title'),
      to: localePath({ name: 'api' }),

      children: [
        {
          label: t('api.get-started.authentication'),
          to: localePath({
            name: 'api-slug',
            params: { slug: 'authentication' },
          }),
        },
        {
          label: t('api.get-started.errors'),
          to: localePath({
            name: 'api-slug',
            params: { slug: 'errors' },
          }),
        },
        {
          label: t('api.get-started.rate-limits'),
          to: localePath({
            name: 'api-slug',
            params: { slug: 'rate-limits' },
          }),
        },
        {
          label: t('api.get-started.webhooks'),
          to: localePath({
            name: 'api-slug',
            params: { slug: 'webhooks' },
          }),
        },
        {
          label: t('api.get-started.datapoints'),
          to: localePath({
            name: 'api-slug',
            params: { slug: 'datapoints' },
          }),
        },
      ],
    },

    // Companies section
    ...(groupedEndpoints?.value?.companies?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-building-office',
            iconActive: !!groupedEndpoints.value.companies.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.companies.title'),
            to: localePath(groupedEndpoints.value.companies[0]?.loc || '/api/companies'),

            children: groupedEndpoints.value.companies.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
                ...(endpoint.isNew ? { badge: 'New' } : {}),
              }),
            ),
          },
        ]
      : []),

    // Industries section
    ...(groupedEndpoints?.value?.industries?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-building-office-2',
            iconActive: !!groupedEndpoints.value.industries.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.industries.title'),
            to: localePath(groupedEndpoints.value.industries[0]?.loc || '/api/industries'),

            children: groupedEndpoints.value.industries.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

    // Job Titles section
    ...(groupedEndpoints?.value?.['job-titles']?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-user-group',
            iconActive: !!groupedEndpoints.value['job-titles'].find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.jobTitles.title'),
            to: localePath(groupedEndpoints.value['job-titles'][0]?.loc || '/api/job-titles'),

            children: groupedEndpoints.value['job-titles'].map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

    // Lists section
    ...(groupedEndpoints?.value?.lists?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-list-bullet',
            iconActive: !!groupedEndpoints.value.lists.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.lists.title'),
            to: localePath(groupedEndpoints.value.lists[0]?.loc || '/api/lists'),

            children: groupedEndpoints.value.lists.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

    // Locations section
    ...(groupedEndpoints?.value?.locations?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-map-pin',
            iconActive: !!groupedEndpoints.value.locations.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.locations.title'),
            to: localePath(groupedEndpoints.value.locations[0]?.loc || '/api/locations'),

            children: groupedEndpoints.value.locations.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

    // Actions section
    ...(groupedEndpoints?.value?.actions?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-bolt',
            iconActive: !!groupedEndpoints.value.actions.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.actions.title'),
            to: localePath(groupedEndpoints.value.actions[0]?.loc || '/api/actions'),

            children: groupedEndpoints.value.actions.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

    // Teams section
    ...(groupedEndpoints?.value?.teams?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-users',
            iconActive: !!groupedEndpoints.value.teams.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.team.title'),
            to: localePath(groupedEndpoints.value.teams[0]?.loc || '/api/teams'),

            children: groupedEndpoints.value.teams.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

    // Technologies section
    ...(groupedEndpoints?.value?.technologies?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-cpu-chip',
            iconActive: !!groupedEndpoints.value.technologies.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.technologies.title'),
            to: localePath(groupedEndpoints.value.technologies[0]?.loc || '/api/technologies'),

            children: groupedEndpoints.value.technologies.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

    // Utilities section
    ...(groupedEndpoints?.value?.utilities?.length
      ? [
          {
            active: false,
            icon: 'i-heroicons-wrench-screwdriver',
            iconActive: !!groupedEndpoints.value.utilities.find(endpoint => route.path.endsWith(endpoint.loc)),
            label: t('api.navigation.utilities.title'),
            to: localePath(groupedEndpoints.value.utilities[0]?.loc || '/api/utilities'),

            children: groupedEndpoints.value.utilities.map(
              endpoint => ({
                label: t(endpoint.text),
                to: localePath(endpoint.loc),
              }),
            ),
          },
        ]
      : []),

  ]
})

// Close mobile nav when route changes
watch(
  () => route.path,
  () => (isMobileNavOpen.value = false),
)
</script>

<template>
  <UContainer class="py-8">
    <div class="px-4 pb-4 lg:hidden">
      <UButton
        class="w-full"
        :icon="isMobileNavOpen ? 'i-heroicons-x-mark' : 'i-heroicons-bars-3'"
        size="sm"
        variant="subtle"
        @click="isMobileNavOpen = !isMobileNavOpen"
      >
        <span class="flex items-center justify-between">
          {{ isMobileNavOpen ? t('globals.close') : t('navigation') }}

          <span class="flex items-center gap-2">
            &nbsp;
          </span>
        </span>
      </UButton>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-[250px_1fr]">
      <div
        class="overflow-hidden transition-all duration-300 ease-in-out lg:hidden"
        :class="isMobileNavOpen ? 'opacity-100' : 'max-h-0 opacity-0'"
      >
        <div class="px-4 pb-4">
          <Aside :links="apiLinks" />
        </div>
      </div>

      <div class="hidden lg:block">
        <Aside :links="apiLinks" />
      </div>

      <main class="overflow-hidden py-4 lg:py-8">
        <UContainer>
          <NuxtPage />
        </UContainer>
      </main>
    </div>
  </UContainer>
</template>

<i18n lang="json">
{
  "en": {
    "api": {
      "get-started": {
        "authentication": "Authentication",
        "datapoints": "Datapoints",
        "errors": "Errors",
        "rate-limits": "Rate limits",
        "title": "Get started",
        "webhooks": "Webhooks"
      },
      "navigation": {
        "actions": {
          "endpoints": {
            "fetchActions": "Fetch actions",
            "requestAction": "Request action"
          },
          "title": "Actions"
        },
        "companies": {
          "endpoints": {
            "ask": "Ask company",
            "count": "Count companies",
            "enrichFromDomain": "Enrich from domain",
            "enrichFromEmail": "Enrich from email",
            "enrichFromSocialNetworkUrl": "Enrich from social network",
            "exportAnalytics": "Export analytics",
            "fetchAnalytics": "Fetch analytics",
            "fetchContext": "Fetch context",
            "findEmailPatterns": "Find email patterns",
            "search": "Search companies",
            "searchByName": "Search by name",
            "searchByPrompt": "Search by prompt",
            "searchSimilar": "Search similar"
          },
          "title": "Companies"
        },
        "industries": {
          "endpoints": {
            "findSimilar": "Find similar industries",
            "search": "Search industries"
          },
          "title": "Industries"
        },
        "jobTitles": {
          "endpoints": {
            "enrich": "Enrich job title"
          },
          "title": "Job Titles"
        },
        "lists": {
          "endpoints": {
            "createList": "Create list",
            "fetchCompanies": "Fetch companies",
            "fetchLists": "Fetch lists",
            "toggleCompanies": "Toggle companies"
          },
          "title": "Lists"
        },
        "locations": {
          "endpoints": {
            "searchCities": "Search cities",
            "searchContinents": "Search continents",
            "searchCounties": "Search counties",
            "searchCountries": "Search countries",
            "searchStates": "Search states"
          },
          "title": "Locations"
        },
        "team": {
          "endpoints": {
            "fetchTeam": "Fetch team"
          },
          "title": "Teams"
        },
        "technologies": {
          "endpoints": {
            "search": "Search technologies"
          },
          "title": "Technologies"
        },
        "utilities": {
          "endpoints": {
            "fetchApiHealth": "API health",
            "fetchOpenApi": "OpenAPI spec"
          },
          "title": "Utilities"
        }
      }
    },
    "navigation": "Navigation"
  },
  "es": {
    "api": {
      "get-started": {
        "authentication": "Autenticación",
        "datapoints": "Datapoints (empresa)",
        "errors": "Errores",
        "rate-limits": "Límites de velocidad",
        "title": "Empezar",
        "webhooks": "Webhooks"
      },
      "navigation": {
        "actions": {
          "endpoints": {
            "fetchActions": "Obtener acciones",
            "requestAction": "Solicitar acción"
          },
          "title": "Acciones"
        },
        "companies": {
          "endpoints": {
            "ask": "Preguntar empresa",
            "count": "Contar empresas",
            "enrichFromDomain": "Enriquecer desde dominio",
            "enrichFromEmail": "Enriquecer desde email",
            "enrichFromSocialNetworkUrl": "Enriquecer desde red social",
            "exportAnalytics": "Exportar analíticas",
            "fetchAnalytics": "Obtener analíticas",
            "fetchContext": "Obtener contexto",
            "findEmailPatterns": "Encontrar patrones de email",
            "search": "Buscar empresas",
            "searchByName": "Buscar por nombre",
            "searchByPrompt": "Buscar por prompt",
            "searchSimilar": "Buscar similares"
          },
          "title": "Empresas"
        },
        "industries": {
          "endpoints": {
            "findSimilar": "Encontrar industrias similares",
            "search": "Buscar industrias"
          },
          "title": "Industrias"
        },
        "jobTitles": {
          "endpoints": {
            "enrich": "Enriquecer título de trabajo"
          },
          "title": "Títulos de trabajo"
        },
        "lists": {
          "endpoints": {
            "createList": "Crear lista",
            "fetchCompanies": "Obtener empresas",
            "fetchLists": "Obtener listas",
            "toggleCompanies": "Alternar empresas"
          },
          "title": "Listas"
        },
        "locations": {
          "endpoints": {
            "searchCities": "Buscar ciudades",
            "searchContinents": "Buscar continentes",
            "searchCounties": "Buscar condados",
            "searchCountries": "Buscar países",
            "searchStates": "Buscar estados"
          },
          "title": "Ubicaciones"
        },
        "team": {
          "endpoints": {
            "fetchTeam": "Obtener equipo"
          },
          "title": "Equipos"
        },
        "technologies": {
          "endpoints": {
            "search": "Buscar tecnologías"
          },
          "title": "Tecnologías"
        },
        "utilities": {
          "endpoints": {
            "fetchApiHealth": "Salud de API",
            "fetchOpenApi": "Especificación openapi"
          },
          "title": "Utilidades"
        }
      }
    },
    "navigation": "Navegación"
  },
  "fr": {
    "api": {
      "get-started": {
        "authentication": "Authentification",
        "datapoints": "Datapoints (entreprise)",
        "errors": "Erreurs",
        "rate-limits": "Limites de débit",
        "title": "Commencer",
        "webhooks": "Webhooks"
      },
      "navigation": {
        "actions": {
          "endpoints": {
            "fetchActions": "Récupérer actions",
            "requestAction": "Demander action"
          },
          "title": "Actions"
        },
        "companies": {
          "endpoints": {
            "ask": "Demander entreprise",
            "count": "Compter entreprises",
            "enrichFromDomain": "Enrichir depuis domaine",
            "enrichFromEmail": "Enrichir depuis email",
            "enrichFromSocialNetworkUrl": "Enrichir depuis réseau social",
            "exportAnalytics": "Exporter analytiques",
            "fetchAnalytics": "Récupérer analytiques",
            "fetchContext": "Récupérer contexte",
            "findEmailPatterns": "Trouver modèles email",
            "search": "Rechercher entreprises",
            "searchByName": "Rechercher par nom",
            "searchByPrompt": "Rechercher par prompt",
            "searchSimilar": "Rechercher similaires"
          },
          "title": "Entreprises"
        },
        "industries": {
          "endpoints": {
            "findSimilar": "Trouver industries similaires",
            "search": "Rechercher industries"
          },
          "title": "Industries"
        },
        "jobTitles": {
          "endpoints": {
            "enrich": "Enrichir titre de poste"
          },
          "title": "Titres de poste"
        },
        "lists": {
          "endpoints": {
            "createList": "Créer liste",
            "fetchCompanies": "Récupérer entreprises",
            "fetchLists": "Récupérer listes",
            "toggleCompanies": "Basculer entreprises"
          },
          "title": "Listes"
        },
        "locations": {
          "endpoints": {
            "searchCities": "Rechercher villes",
            "searchContinents": "Rechercher continents",
            "searchCounties": "Rechercher comtés",
            "searchCountries": "Rechercher pays",
            "searchStates": "Rechercher états"
          },
          "title": "Emplacements"
        },
        "team": {
          "endpoints": {
            "fetchTeam": "Récupérer équipe"
          },
          "title": "Équipes"
        },
        "technologies": {
          "endpoints": {
            "search": "Rechercher technologies"
          },
          "title": "Technologies"
        },
        "utilities": {
          "endpoints": {
            "fetchApiHealth": "Santé API",
            "fetchOpenApi": "Spécification OpenAPI"
          },
          "title": "Utilitaires"
        }
      }
    },
    "navigation": "Navigation"
  }
}
</i18n>
